import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { PersonalInfoService } from '@/integrations/supabase/services';
import { useNavigate } from 'react-router-dom';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { DashboardLayout } from '@/components/DashboardLayout';
import ProfileImageUpload from '@/components/ProfileImageUpload';

const PersonalInfo = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    nationality: '',
    passportNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { completionStatus, loading: statusLoading } = useCompletionStatus();

  useEffect(() => {
    if (user) {
      loadPersonalInfo();
    }
  }, [user]);

  const loadPersonalInfo = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const personalInfoList = await PersonalInfoService.getByUserId(user.uid);
      const data = personalInfoList.length > 0 ? personalInfoList[0] : null;

      setFormData({
        fullName: data?.full_name || '',
        phone: data?.phone || '',
        address: data?.address || '',
        dateOfBirth: data?.date_of_birth || '',
        nationality: data?.nationality || '',
        passportNumber: data?.passport_number || ''
      });
      setProfileImageUrl(data?.profile_image_url || '');
    } catch (error) {
      console.error('Error loading personal info:', error);
      toast({
        title: "Error loading data",
        description: "Could not load your personal information.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setSubmitting(true);

    try {
      // Get existing personal info to check if we need to update or create
      const existingPersonalInfo = await PersonalInfoService.getByUserId(user.uid);
      const existingId = existingPersonalInfo.length > 0 ? existingPersonalInfo[0].id : null;

      await PersonalInfoService.upsert(user.uid, {
        full_name: formData.fullName,
        phone: formData.phone,
        address: formData.address,
        date_of_birth: formData.dateOfBirth,
        nationality: formData.nationality,
        passport_number: formData.passportNumber,
      }, existingId);

      toast({
        title: "Personal information saved",
        description: "Your information has been successfully updated.",
      });

      // Navigate to next step
      navigate('/documents');
    } catch (error) {
      console.error('Error saving personal info:', error);
      toast({
        title: "Error saving information",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Personal Information">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Personal Information">
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>
            Fill out your personal details for the Karta Pobytu application
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Profile Image Upload - Outside form to prevent submission */}
          <div className="flex justify-center mb-6">
            <ProfileImageUpload
              currentImageUrl={profileImageUrl}
              onImageUpdate={setProfileImageUrl}
            />
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nationality">Nationality</Label>
                <Input
                  id="nationality"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="passportNumber">Passport Number</Label>
              <Input
                id="passportNumber"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="flex space-x-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Back to Dashboard
              </Button>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default PersonalInfo;
